import React from 'react';
import { Link } from 'react-router-dom';
import { routes } from '../../config/utility';

const PublicFooter = ({ showSupportCTA = true }) => {
    return (
        <footer className="mt-auto relative">
            {/* SVG Wave Top */}
            {/* Help Section with Wave Background */}
            {showSupportCTA && (
                <div className="relative bg-white flex flex-col md:block">
                    {/* Wave Background */}
                    <div className="z-0 -mt-10 md:mt-0">
                        <img src="/assets/guest/footer.svg" alt="Wave Top" className="h-[330px] sm:w-full sm:h-full sm:object-cover" />
                    </div>

                    {/* Need Help Content */}
                    <div className="relative md:absolute bottom-0 left-0 right-0 z-10 pt-0 pb-4 md:pt-32 md:pb-16">
                        <div className="container mx-auto px-6 max-w-3xl">
                            <div className="text-center">
                                <h2 className="text-xl md:text-4xl font-bold text-black md:text-white mb-2">Need Help or Have a Question?</h2>
                                <p className="text-black/80 md:text-white/90 mb-6 text-xs md:text-base">Chat with our support team. We're here to help you get the most out of Popout.</p>
                                <Link to={routes.login} className="inline-block text-xs px-4 py-2 sm:px-8 sm:py-3 bg-[#8B5CF6] text-white md:bg-white md:text-[#8B5CF6] sm:text-xl font-medium rounded-full hover:bg-[#7C3AED] md:hover:bg-gray-100 transition-colors">
                                    Get Started
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Footer Content */}
            <div className="bg-white pt-16 pb-8">
                <div className="container w-full mx-auto px-6">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
                        {/* Logo Column */}
                        <div className="col-span-2 md:col-span-1">
                            <img src="/assets/FULLLOGO.svg" alt="Popout" className="h-8 w-auto mb-6" />
                        </div>

                        {/* Company Links */}
                        <div>
                            <h3 className="font-medium text-gray-800 mb-4">Company</h3>
                            <ul className="space-y-3">
                                <li><a href="/for-parents" className="text-sm text-gray-500 hover:text-gray-800">For Parents</a></li>
                                <li><a href="/for-kids" className="text-sm text-gray-500 hover:text-gray-800">For Kids</a></li>
                                <li><a href="/for-organizers" className="text-sm text-gray-500 hover:text-gray-800">For Organizers</a></li>
                            </ul>
                        </div>

                        {/* Products Links */}
                        <div>
                            <h3 className="font-medium text-gray-800 mb-4">Products</h3>
                            <ul className="space-y-3">
                                <li><a href="/web-application" className="text-sm text-gray-500 hover:text-gray-800">Web Application</a></li>
                                <li><a href="/mobile-application" className="text-sm text-gray-500 hover:text-gray-800">Mobile Application</a></li>
                                <li><a href="/pricing" className="text-sm text-gray-500 hover:text-gray-800">Pricing</a></li>
                            </ul>
                        </div>

                        {/* Resources Links */}
                        <div>
                            <h3 className="font-medium text-gray-800 mb-4">Resources</h3>
                            <ul className="space-y-3">
                                <li><a href="/privacy-policy" className="text-sm text-gray-500 hover:text-gray-800">Privacy Policy</a></li>
                                <li><a href="/pricing" className="text-sm text-gray-500 hover:text-gray-800">Pricing</a></li>
                                <li>
                                    <Link 
                                        to={routes.contact} 
                                        className="text-sm text-gray-500 hover:text-gray-800"
                                        onClick={() => {
                                            // Scroll to top immediately when link is clicked
                                            window.scrollTo(0, 0);
                                            document.documentElement.scrollTop = 0;
                                            document.body.scrollTop = 0;
                                        }}
                                    >
                                        Help Center
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        <div className="col-span-2 sm:col-span-1">
                            <h3 className="text-base text-gray-800 mb-2">Want to get our updates?</h3>
                            <div className="mb-2 md:mb-8">
                                <div className="flex flex-wrap items-center text-xl font-medium break-all">
                                    <span>Support@</span>
                                    <span className="text-[#A855F7]">p</span>
                                    <span className="text-[#EC4899]">o</span>
                                    <span className="text-[#F472B6]">p</span>
                                    <span className="text-[#10B981]">o</span>
                                    <span className="text-[#3B82F6]">u</span>
                                    <span className="text-[#6366F1]">t</span>
                                    <span>.com</span>
                                </div>
                            </div>
                        </div>
                    </div>



                    {/* Bottom Bar with Copyright */}
                    <div className="mt-6 md:mt-16 pt-8 border-t border-gray-100">
                        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                            {/* Copyright */}
                            <div>
                                <p className="text-sm text-gray-400">© Copyrights {new Date().getFullYear()}, Popout</p>
                            </div>

                            {/* Social Icons */}
                            <div className="flex items-center space-x-6">
                                <a href="https://instagram.com" className="text-gray-400 hover:text-gray-600" aria-label="Instagram">
                                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" /></svg>
                                </a>
                                <a href="https://x.com" className="text-gray-400 hover:text-gray-600" aria-label="Twitter">
                                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" /></svg>
                                </a>
                                <a href="https://youtube.com" className="text-gray-400 hover:text-gray-600" aria-label="YouTube">
                                    <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" /></svg>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};


export default PublicFooter;